import React, { useState, useRef, useCallback } from "react";
import { Upload, Camera, Loader2, Sparkles, X, Home, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";

function renderContent(text) {
  const urlRegex = /(https?:\/\/[^\s,)]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 decoration-accent/60 hover:text-accent transition-colors break-all font-medium">{part}</a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

export default function StyleMyHome() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [roomType, setRoomType] = useState("");
  const [notes, setNotes] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const fileRef = useRef(null);

  const handleFile = useCallback((f) => {
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png", "webp"].includes(ext)) {
      toast.error("Only JPG, PNG, or WebP images are accepted");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB)");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult("");
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const analyze = async () => {
    if (!file) return toast.error("Please upload a room photo first");
    setAnalyzing(true);
    setResult("");

    const formData = new FormData();
    formData.append("file", file);
    if (roomType) formData.append("room_type", roomType);
    if (notes) formData.append("notes", notes);

    try {
      const res = await fetch(`${API}/style-home/analyze`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Analysis failed");
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.type === "delta") {
              setResult(prev => prev + payload.content);
            } else if (payload.type === "done") {
              if (payload.image_url) setImageUrl(payload.image_url);
            } else if (payload.type === "error") {
              setResult(payload.content);
            }
          } catch {}
        }
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setAnalyzing(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult("");
    setImageUrl("");
    setNotes("");
    setRoomType("");
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12" data-testid="style-my-home-page">
      {/* Header */}
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">AI-Powered Styling</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-display text-primary">Style My Home</h1>
        <p className="mt-4 text-muted-foreground max-w-lg mx-auto">
          Upload a photo of your room and AethelAura will suggest the perfect Aethel Decor pieces to elevate your space.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left — Upload */}
        <div className="space-y-4">
          {/* Drop zone */}
          {!preview ? (
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-border hover:border-primary/40 bg-secondary/30 transition-colors cursor-pointer flex flex-col items-center justify-center text-center py-20 px-8"
              data-testid="style-upload-zone"
            >
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                <Camera size={28} className="text-primary" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">Upload a room photo</p>
              <p className="text-xs text-muted-foreground">Drag & drop or click to browse</p>
              <p className="text-[10px] text-muted-foreground mt-2">JPG, PNG, WebP — max 8MB</p>
              <input
                ref={fileRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={(e) => handleFile(e.target.files[0])}
                className="hidden"
                data-testid="style-file-input"
              />
            </div>
          ) : (
            <div className="relative group">
              <img
                src={preview}
                alt="Your room"
                className="w-full aspect-[4/3] object-cover border border-border"
                data-testid="style-preview-image"
              />
              <button
                onClick={reset}
                className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove image"
              >
                <X size={16} className="text-muted-foreground" />
              </button>
            </div>
          )}

          {/* Options */}
          <div className="grid grid-cols-2 gap-3">
            <select
              value={roomType}
              onChange={(e) => setRoomType(e.target.value)}
              className="bg-muted px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary"
              data-testid="style-room-type"
            >
              <option value="">Room type (optional)</option>
              <option value="Living Room">Living Room</option>
              <option value="Bedroom">Bedroom</option>
              <option value="Dining Room">Dining Room</option>
              <option value="Kitchen">Kitchen</option>
              <option value="Balcony">Balcony</option>
              <option value="Study">Study / Office</option>
              <option value="Pooja Room">Pooja Room</option>
            </select>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any preferences? (e.g. minimal, festive)"
              className="bg-muted px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary"
              data-testid="style-notes"
            />
          </div>

          {/* Analyze button */}
          <button
            onClick={analyze}
            disabled={!file || analyzing}
            className="w-full bg-primary text-primary-foreground py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-50"
            data-testid="style-analyze-btn"
          >
            {analyzing ? (
              <><Loader2 size={16} className="animate-spin" /> AethelAura is analyzing your room...</>
            ) : (
              <><Sparkles size={16} /> Get Styling Suggestions</>
            )}
          </button>
        </div>

        {/* Right — Results */}
        <div>
          {!result && !analyzing ? (
            <div className="border border-border bg-secondary/20 h-full min-h-[300px] flex flex-col items-center justify-center text-center px-8">
              <Home size={36} className="text-muted-foreground/30 mb-4" />
              <p className="text-sm text-muted-foreground">Upload a photo and AethelAura will suggest decor that's perfect for your space.</p>
            </div>
          ) : (
            <div className="border border-border bg-card p-6 min-h-[300px]" data-testid="style-results">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
                <Sparkles size={16} className="text-accent" />
                <p className="text-sm font-semibold text-primary">AethelAura's Styling Suggestions</p>
                {!analyzing && result && (
                  <button onClick={reset} className="ml-auto text-xs text-muted-foreground hover:text-primary flex items-center gap-1">
                    <RefreshCw size={12} /> Try another
                  </button>
                )}
              </div>

              <div className="text-sm leading-relaxed text-foreground whitespace-pre-wrap" data-testid="style-analysis-text">
                {renderContent(result)}
                {analyzing && !result && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 size={14} className="animate-spin" />
                    <span>Analyzing your room...</span>
                  </div>
                )}
                {analyzing && result && (
                  <span className="inline-flex gap-1 ml-1">
                    <span className="w-1.5 h-1.5 bg-accent rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 bg-accent rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 bg-accent rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
